import CloseIcon from "@mui/icons-material/Close";
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	MenuItem,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
	useTheme,
	InputAdornment,
} from "@mui/material";
import { addDays, addMonths, format, parseISO } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useEffect, useMemo, useState } from "react";
import logo from "../../../../assets/logo_report.png";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { Environment } from "../../../../shared/environment";
import { VForm, VMaskTextField } from "../../../../shared/forms";
import { useToast } from "../../../../shared/hooks";
import { trackAsync } from "../../../../shared/utils/telemetry";
import { ISinisterList, ISinisterValues, SinisterService, getSinisterValueAmount } from "../../../../shared/services/api/sinister/SinisterService";
import {
	NegotiationInstallmentIntervalType,
	NegotiationPaymentMethod,
	SinisterNegotiationService,
} from "../../../../shared/services/api/sinister/SinisterNegotiationService";
import { formatScaleValueBR } from "../../../../shared/utils/formatScaleValue";
import { globalMaskDocument, globalRemoveMaskReal2, globalTranslate, maskDocumentLGPD } from "../../../../shared/utils";
import { getNextBusinessDay } from "../../../../shared/utils/dateUtils";
import { PAYMENT_METHOD_OPTIONS, getPaymentMethodLabel } from "../../constants/paymentMethodOptions";
import { ModalSinisterValues } from "./ModalSinisterValues";

interface Props {
	open: boolean;
	sinisterId: string;
	onClose: () => void;
}

export const ModalNegotiateDeal = ({ open, sinisterId, onClose }: Props) => {
	const theme = useTheme();
	const toastHandler = useToast();

	const [step, setStep] = useState<"build" | "review">("build");
	const [sinisterInfo, setSinisterInfo] = useState<ISinisterList | null>(null);
	const [values, setValues] = useState<ISinisterValues[]>([]);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [paymentMethod, setPaymentMethod] = useState<NegotiationPaymentMethod>("boleto");
	const [installmentsCount, setInstallmentsCount] = useState(1);
	const [installmentIntervalType, setInstallmentIntervalType] = useState<NegotiationInstallmentIntervalType>("monthly");
	const [installmentIntervalDays, setInstallmentIntervalDays] = useState(30);
	const [discountPercent, setDiscountPercent] = useState(0);
	const [downPaymentInput, setDownPaymentInput] = useState("0,00");
	const [downPaymentFormKey, setDownPaymentFormKey] = useState(0);
	const [downPaymentDate, setDownPaymentDate] = useState("");
	const [dueDate, setDueDate] = useState("");
	const [openDebtDetails, setOpenDebtDetails] = useState(false);
	const [openInstallmentsPreview, setOpenInstallmentsPreview] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (!open) return;

		setStep("build");
		setDueDate(format(getNextBusinessDay(), "yyyy-MM-dd"));
		setDownPaymentInput("0,00");
		setDownPaymentFormKey((prev) => prev + 1);
		setDownPaymentDate(format(getNextBusinessDay(), "yyyy-MM-dd"));
		setIsLoading(true);
		Promise.all([
			SinisterService.getAllValues(1, "", "", "", sinisterId, Environment.LIMITE_LINHAS_VALORES_BORDEROS).then((result) => {
				if (result instanceof Error) return;
				const notCanceled = result.data.filter((value) => value.status !== "canceled");
				setValues(notCanceled);
				setSelectedIds(new Set(notCanceled.map((value) => value.id!).filter(Boolean)));
			}),
			SinisterService.getById(sinisterId).then((result) => {
				if (result instanceof Error) return;
				setSinisterInfo(result);
			}),
		]).finally(() => setIsLoading(false));
	}, [open, sinisterId]);

	const selectedValues = values.filter((value) => value.id && selectedIds.has(value.id));
	const subtotalValue = selectedValues.reduce((acc, value) => acc + getSinisterValueAmount(value), 0);
	const discountAmount = subtotalValue * (Math.min(Math.max(discountPercent, 0), 100) / 100);
	const totalValue = subtotalValue - discountAmount;
	const downPaymentValue = useMemo(() => {
		const cleaned = globalRemoveMaskReal2(String(downPaymentInput || "0"));
		const parsed = Number(cleaned);
		return Number.isFinite(parsed) ? Math.max(0, Math.min(totalValue, parsed)) : 0;
	}, [downPaymentInput, totalValue]);
	const remainingValue = Math.max(totalValue - downPaymentValue, 0);
	const installmentValue = installmentsCount > 0 ? remainingValue / installmentsCount : 0;

	const installmentSchedule = useMemo(() => {
		if (!dueDate) return [];
		const baseDate = parseISO(dueDate);
		return Array.from({ length: installmentsCount }).map((_, i) => ({
			number: i + 1,
			dueDate: installmentIntervalType === "monthly" ? addMonths(baseDate, i) : addDays(baseDate, i * installmentIntervalDays),
			value: installmentValue,
		}));
	}, [dueDate, installmentsCount, installmentIntervalType, installmentIntervalDays, installmentValue]);

	const toggleSelected = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const handleConfirm = async () => {
		setIsSubmitting(true);
		const result = await trackAsync(
			"negotiation.create_deal",
			() =>
				SinisterNegotiationService.createDeal(sinisterId, {
					valueIds: Array.from(selectedIds),
					paymentMethod,
					installmentsCount,
					installmentIntervalType: installmentsCount > 1 ? installmentIntervalType : undefined,
					installmentIntervalDays: installmentsCount > 1 && installmentIntervalType === "days" ? installmentIntervalDays : undefined,
					discountPercent: discountPercent > 0 ? discountPercent : undefined,
					downPaymentValue: downPaymentValue > 0 ? downPaymentValue : undefined,
					downPaymentDate: downPaymentValue > 0 ? downPaymentDate : undefined,
					dueDate,
					totalValue,
				}),
			{ sinisterId }
		);
		setIsSubmitting(false);

		if (result instanceof Error) {
			toastHandler.present({ type: "error", messageOrContent: result.message, position: "top-right" });
			return;
		}

		toastHandler.present({ type: "success", messageOrContent: "Acordo registrado com sucesso!", position: "top-right" });
		onClose();
	};

	const handleExportMirror = () => {
		try {
			const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
			const marginX = 40;
			const rightX = 555;
			let y = 20;

			pdf.addImage(logo, "PNG", marginX, y, 120, 30);
			pdf.setFontSize(9);
			pdf.setTextColor(120);
			pdf.text(`Solicitado em: ${format(new Date(), "dd/MM/yyyy - HH:mm")}`, rightX, 35, { align: "right" });

			y += 30 + 25;

			pdf.setFontSize(13);
			pdf.setFont("helvetica", "bold");
			pdf.setTextColor(40);
			pdf.text("Espelho da Negociação", marginX, y);
			y += 20;

			pdf.setFontSize(11);
			pdf.text(sinisterInfo?.proposal?.name ?? "-", marginX, y);
			y += 14;

			pdf.setFont("helvetica", "normal");
			pdf.setFontSize(9);
			pdf.setTextColor(100);
			pdf.text(sinisterInfo?.proposal?.document ? maskDocumentLGPD(sinisterInfo.proposal.document) : "-", marginX, y);
			y += 14;

			pdf.text(`Imobiliária: ${sinisterInfo?.client?.name ?? sinisterInfo?.client?.trade_name ?? "-"}`, marginX, y);
			y += 16;

			pdf.setDrawColor(224);
			pdf.setLineWidth(0.5);
			pdf.line(marginX, y, rightX, y);
			y += 22;

			pdf.setFont("helvetica", "bold");
			pdf.setFontSize(10);
			pdf.setTextColor(40);
			pdf.text("Condições de Pagamento", marginX, y);
			y += 16;

			pdf.setFont("helvetica", "normal");
			pdf.setFontSize(9);
			pdf.setTextColor(60);
			const conditionLines = [
				`Forma de pagamento: ${getPaymentMethodLabel(paymentMethod)}`,
				`Desconto: ${discountPercent > 0 ? `${discountPercent}% (${formatScaleValueBR(String(discountAmount))})` : "Nenhum"}`,
				`Entrada: ${downPaymentValue > 0 ? formatScaleValueBR(String(downPaymentValue)) : "Nenhuma"}`,
				...(downPaymentValue > 0 ? [`Data da entrada: ${downPaymentDate ? format(parseISO(downPaymentDate), "dd/MM/yyyy") : "-"}`] : []),
				`Quantidade de parcelas: ${installmentsCount}`,
				`Data do 1º pagamento: ${dueDate ? format(parseISO(dueDate), "dd/MM/yyyy") : "-"}`,
			];
			conditionLines.forEach((line) => {
				pdf.text(line, marginX, y);
				y += 14;
			});
			y += 4;

			pdf.line(marginX, y, rightX, y);
			y += 22;

			pdf.setFont("helvetica", "bold");
			pdf.setFontSize(10);
			pdf.setTextColor(40);
			pdf.text("Débitos Incluídos", marginX, y);
			y += 8;

			autoTable(pdf, {
				startY: y,
				margin: { left: marginX, right: 595 - rightX },
				head: [["Descrição", "Referência", "Vencimento", "Valor"]],
				body: selectedValues.map((value) => [
					globalTranslate(value.typeId),
					value.reference,
					format(parseISO(value.dueDate), "dd/MM/yyyy"),
					formatScaleValueBR(String(getSinisterValueAmount(value))),
				]),
				styles: { font: "helvetica", fontSize: 9 },
				headStyles: { fillColor: [79, 129, 189], textColor: 255 },
				alternateRowStyles: { fillColor: [245, 245, 245] },
			});

			y = (pdf as any).lastAutoTable.finalY + 25;

			pdf.setFont("helvetica", "bold");
			pdf.setFontSize(10);
			pdf.setTextColor(40);
			pdf.text("Parcelas de Pagamento", marginX, y);
			y += 8;

			if (installmentsCount === 1) {
				pdf.setFont("helvetica", "normal");
				pdf.setFontSize(9);
				pdf.setTextColor(60);
				pdf.text(
					`À Vista — Vencimento em ${dueDate ? format(parseISO(dueDate), "dd/MM/yyyy") : "-"} — ${formatScaleValueBR(String(installmentValue))}`,
					marginX,
					y + 12
				);
				y += 26;
			} else {
				autoTable(pdf, {
					startY: y,
					margin: { left: marginX, right: 595 - rightX },
					head: [["Parcela", "Vencimento", "Valor"]],
					body: installmentSchedule.map((installment) => [
						String(installment.number),
						format(installment.dueDate, "dd/MM/yyyy"),
						formatScaleValueBR(String(installment.value)),
					]),
					styles: { font: "helvetica", fontSize: 9 },
					headStyles: { fillColor: [79, 129, 189], textColor: 255 },
					alternateRowStyles: { fillColor: [245, 245, 245] },
				});
				y = (pdf as any).lastAutoTable.finalY + 25;
			}

			pdf.setDrawColor(224);
			pdf.line(marginX, y, rightX, y);
			y += 20;

			pdf.setFont("helvetica", "bold");
			pdf.setFontSize(11);
			pdf.setTextColor(40);
			pdf.text("Valor Total do Acordo", marginX, y);
			pdf.text(formatScaleValueBR(String(totalValue)), rightX, y, { align: "right" });

			const pageCount = pdf.getNumberOfPages();
			for (let i = 1; i <= pageCount; i++) {
				pdf.setPage(i);
				pdf.setFontSize(9);
				pdf.setTextColor(150);
				pdf.text(`Página ${i} de ${pageCount}`, pdf.internal.pageSize.getWidth() / 2, pdf.internal.pageSize.getHeight() - 20, { align: "center" });
			}

			pdf.save(`espelho-negociacao-${sinisterId}.pdf`);
		} catch (error) {
			console.error("Erro ao gerar espelho da negociação:", error);
			toastHandler.present({ type: "error", messageOrContent: "Erro ao gerar o espelho da negociação.", position: "top-right" });
		}
	};

	const sectionTitleSx = { mb: 1.5, textTransform: "uppercase" as const };

	return (
		<>
			<LoadingComp isopen={isLoading || isSubmitting} />
			<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
					<Box>
						<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
							{step === "build" ? "Negociação do Sinistro" : "Revisão do Acordo"}
						</Typography>

						{sinisterInfo?.proposal && (
							<Typography variant="body2" color="text.secondary" mt={0.5}>
								{sinisterInfo.proposal.name} ({globalMaskDocument(sinisterInfo.proposal.document)})
							</Typography>
						)}
					</Box>

					<IconButton onClick={onClose} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers sx={{ px: { xs: 3, md: 4 }, py: { xs: 3, md: 4 }, bgcolor: "background.paper" }}>
					{step === "build" ? (
						<Stack spacing={3}>
							<Box>
								<Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
									<Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: "uppercase" }}>
										Valores da Negociação
									</Typography>
									<Stack direction="row" spacing={1}>
										<Button size="small" variant="outlined" onClick={() => setOpenDebtDetails(true)}>
											Ver Detalhes da Dívida
										</Button>
										<Button size="small" variant="outlined" disabled={selectedIds.size === 0} onClick={handleExportMirror}>
											Espelhar Negociação
										</Button>
									</Stack>
								</Grid>

								<TableContainer sx={{ maxHeight: 260 }}>
									<Table size="small" stickyHeader>
										<TableHead>
											<TableRow>
												<TableCell padding="checkbox" />
												<TableCell>Descrição</TableCell>
												<TableCell>Referência</TableCell>
												<TableCell align="center">Vencimento</TableCell>
												<TableCell align="right">Valor Original</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{values.map((value) => (
												<TableRow key={value.id} hover>
													<TableCell padding="checkbox">
														<Checkbox
															checked={!!value.id && selectedIds.has(value.id)}
															onChange={() => value.id && toggleSelected(value.id)}
														/>
													</TableCell>
													<TableCell>{globalTranslate(value.typeId)}</TableCell>
													<TableCell>{value.reference}</TableCell>
													<TableCell align="center">{format(parseISO(value.dueDate), "dd/MM/yyyy")}</TableCell>
													<TableCell align="right">{formatScaleValueBR(value.value)}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>

							<Divider />

							<Stack
								direction={{ xs: "column", md: "row" }}
								spacing={3}
								divider={<Divider orientation="vertical" flexItem sx={{ display: { xs: "none", md: "block" } }} />}
							>
								<Box flex={1}>
									<Typography variant="subtitle2" color="primary" fontWeight={700} sx={sectionTitleSx}>
										Condições do Acordo
									</Typography>

									<Stack spacing={2.5}>
										<TextField
											select
											fullWidth
											size="small"
											label="Forma de pagamento"
											value={paymentMethod}
											onChange={(e) => setPaymentMethod(e.target.value as NegotiationPaymentMethod)}
										>
											{PAYMENT_METHOD_OPTIONS.map((option) => (
												<MenuItem key={option.value} value={option.value}>
													{option.label}
												</MenuItem>
											))}
										</TextField>

										<Box>
											<VForm
												key={downPaymentFormKey}
												onSubmit={() => {}}
												initialData={{ downPaymentValue: "0,00" }}
												onPointerEnterCapture={() => {}}
												onPointerLeaveCapture={() => {}}
												placeholder=""
											>
												<VMaskTextField
													fullWidth
													size="small"
													label="Entrada"
													name="downPaymentValue"
													mask="real"
													placeholder="0,00"
													onChange={(e: any) => setDownPaymentInput(e?.target?.value ?? "")}
													onBlur={(e: any) => setDownPaymentInput(e?.target?.value ?? "")}
													InputProps={{
														startAdornment: <InputAdornment position="start">R$</InputAdornment>,
													}}
												/>
											</VForm>
											<Typography variant="caption" color="text.secondary">
												As parcelas serão calculadas sobre o saldo restante, sem a entrada.
											</Typography>
										</Box>

										{downPaymentValue > 0 && (
											<TextField
												type="date"
												fullWidth
												size="small"
												label="Data da entrada"
												value={downPaymentDate}
												onChange={(e) => setDownPaymentDate(e.target.value)}
												InputLabelProps={{ shrink: true }}
											/>
										)}

										<TextField
											type="number"
											fullWidth
											size="small"
											label="Quantidade de parcelas"
											inputProps={{ min: 1, max: 12 }}
											value={installmentsCount}
											onChange={(e) => setInstallmentsCount(Math.max(1, Math.min(12, Number(e.target.value) || 1)))}
										/>

										<Stack direction="row" spacing={2}>
											<TextField
												type="number"
												fullWidth
												size="small"
												label="Desconto (%)"
												inputProps={{ min: 0, max: 100, step: 0.1 }}
												value={discountPercent}
												onChange={(e) => setDiscountPercent(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
											/>

											{installmentsCount > 1 && (
												<TextField
													select
													fullWidth
													size="small"
													label="Intervalo entre parcelas"
													value={installmentIntervalType}
													onChange={(e) => setInstallmentIntervalType(e.target.value as NegotiationInstallmentIntervalType)}
												>
													<MenuItem value="monthly">Mensal (mesmo dia do mês)</MenuItem>
													<MenuItem value="days">Personalizado (dias corridos)</MenuItem>
												</TextField>
											)}
										</Stack>

										{installmentsCount > 1 && installmentIntervalType === "days" && (
											<TextField
												type="number"
												fullWidth
												size="small"
												label="Dias corridos entre parcelas"
												inputProps={{ min: 1, max: 90 }}
												value={installmentIntervalDays}
												onChange={(e) => setInstallmentIntervalDays(Math.max(1, Math.min(90, Number(e.target.value) || 1)))}
											/>
										)}

										<TextField
											type="date"
											fullWidth
											size="small"
											label="Data de pagamento"
											value={dueDate}
											onChange={(e) => setDueDate(e.target.value)}
											InputLabelProps={{ shrink: true }}
										/>
									</Stack>
								</Box>

								<Box flex={1} display="flex" flexDirection="column">
									<Typography variant="subtitle2" color="primary" fontWeight={700} sx={sectionTitleSx}>
										Resumo da Negociação
									</Typography>

									<Box sx={{ backgroundColor: theme.palette.grey[50], borderRadius: 2, p: 2.5 }}>
										<Stack spacing={1}>
											<Box display="flex" justifyContent="space-between">
												<Typography variant="body2" color="text.secondary">
													Valor atual da dívida
												</Typography>
												<Typography variant="body2" fontWeight={500}>
													{formatScaleValueBR(String(subtotalValue))}
												</Typography>
											</Box>
											{discountPercent > 0 && (
												<Box display="flex" justifyContent="space-between">
													<Typography variant="body2" color="text.secondary">
														Desconto ({discountPercent}%)
													</Typography>
													<Typography variant="body2" fontWeight={500} color="error.main">
														- {formatScaleValueBR(String(discountAmount))}
													</Typography>
												</Box>
											)}
											{downPaymentValue > 0 && (
												<>
													<Box display="flex" justifyContent="space-between">
														<Typography variant="body2" color="text.secondary">
															Entrada {downPaymentDate ? `(${format(parseISO(downPaymentDate), "dd/MM/yyyy")})` : ""}
														</Typography>
														<Typography variant="body2" fontWeight={500} color="error.main">
															- {formatScaleValueBR(String(downPaymentValue))}
														</Typography>
													</Box>
													<Box display="flex" justifyContent="space-between">
														<Typography variant="body2" color="text.secondary">
															Saldo restante
														</Typography>
														<Typography variant="body2" fontWeight={500}>
															{formatScaleValueBR(String(remainingValue))}
														</Typography>
													</Box>
												</>
											)}
											<Box display="flex" justifyContent="space-between">
												<Typography variant="body2" color="text.secondary">
													Forma de pagamento
												</Typography>
												<Typography variant="body2" fontWeight={500}>
													{getPaymentMethodLabel(paymentMethod)}
												</Typography>
											</Box>
											<Box display="flex" justifyContent="space-between">
												<Typography variant="body2" color="text.secondary">
													Vencimento
												</Typography>
												<Typography variant="body2" fontWeight={500}>
													{dueDate ? format(parseISO(dueDate), "dd/MM/yyyy") : "-"}
												</Typography>
											</Box>
										</Stack>

										<Divider sx={{ my: 2 }} />

										<Stack direction="row" justifyContent="space-between" alignItems="flex-end" mb={1.5}>
											<Stack spacing={0.3}>
												<Typography variant="subtitle2" fontWeight={700}>
													Oferta selecionada
												</Typography>
												<Typography variant="body1" fontWeight={600}>
													{installmentsCount === 1 ? "À Vista" : `${installmentsCount}x`} de {formatScaleValueBR(String(installmentValue))}
												</Typography>
											</Stack>

											{installmentsCount > 1 && (
												<Button size="small" variant="text" disabled={!dueDate} onClick={() => setOpenInstallmentsPreview(true)}>
													Ver parcelas
												</Button>
											)}
										</Stack>

										<Box display="flex" justifyContent="space-between" alignItems="center">
											<Typography variant="subtitle2" fontWeight={700}>
												Valor Total do Acordo
											</Typography>
											<Typography variant="subtitle1" fontWeight={700}>
												{formatScaleValueBR(String(totalValue))}
											</Typography>
										</Box>
									</Box>

									<Button
										variant="contained"
										fullWidth
										sx={{ mt: 2.5 }}
										disabled={selectedIds.size === 0 || !dueDate}
										onClick={() => setStep("review")}
									>
										Revisar Acordo e Confirmar
									</Button>
								</Box>
							</Stack>
						</Stack>
					) : (
						<Stack spacing={3}>
							<Box>
								<Typography variant="subtitle2" color="primary" fontWeight={700} sx={sectionTitleSx}>
									Locatário
								</Typography>
								<Typography variant="body1" fontWeight={600}>
									{sinisterInfo?.proposal?.name ?? "-"}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{sinisterInfo?.proposal?.document ? globalMaskDocument(sinisterInfo.proposal.document) : "-"}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									Imobiliária: {sinisterInfo?.client?.name ?? sinisterInfo?.client?.trade_name ?? "-"}
								</Typography>
							</Box>

							<Divider />

							<Box>
								<Typography variant="subtitle2" color="primary" fontWeight={700} sx={sectionTitleSx}>
									Condições de Pagamento
								</Typography>
								<Stack spacing={0.75}>
									<Box display="flex" justifyContent="space-between">
										<Typography variant="body2" color="text.secondary">
											Forma de pagamento
										</Typography>
										<Typography variant="body2" fontWeight={500}>
											{getPaymentMethodLabel(paymentMethod)}
										</Typography>
									</Box>
									<Box display="flex" justifyContent="space-between">
										<Typography variant="body2" color="text.secondary">
											Desconto
										</Typography>
										<Typography variant="body2" fontWeight={500}>
											{discountPercent > 0 ? `${discountPercent}% (${formatScaleValueBR(String(discountAmount))})` : "Nenhum"}
										</Typography>
									</Box>
									<Box display="flex" justifyContent="space-between">
										<Typography variant="body2" color="text.secondary">
											Entrada
										</Typography>
										<Typography variant="body2" fontWeight={500}>
											{downPaymentValue > 0 ? formatScaleValueBR(String(downPaymentValue)) : "Nenhuma"}
										</Typography>
									</Box>
									{downPaymentValue > 0 && (
										<Box display="flex" justifyContent="space-between">
											<Typography variant="body2" color="text.secondary">
												Data da entrada
											</Typography>
											<Typography variant="body2" fontWeight={500}>
												{downPaymentDate ? format(parseISO(downPaymentDate), "dd/MM/yyyy") : "-"}
											</Typography>
										</Box>
									)}
									<Box display="flex" justifyContent="space-between">
										<Typography variant="body2" color="text.secondary">
											Quantidade de parcelas
										</Typography>
										<Typography variant="body2" fontWeight={500}>
											{installmentsCount}
										</Typography>
									</Box>
									<Box display="flex" justifyContent="space-between">
										<Typography variant="body2" color="text.secondary">
											Data do 1º pagamento
										</Typography>
										<Typography variant="body2" fontWeight={500}>
											{dueDate ? format(parseISO(dueDate), "dd/MM/yyyy") : "-"}
										</Typography>
									</Box>
								</Stack>
							</Box>

							<Divider />

							<Box>
								<Typography variant="subtitle2" color="primary" fontWeight={700} sx={sectionTitleSx}>
									Débitos Incluídos
								</Typography>
								<TableContainer>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell>Descrição</TableCell>
												<TableCell>Referência</TableCell>
												<TableCell align="center">Vencimento</TableCell>
												<TableCell align="right">Valor</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{selectedValues.map((value) => (
												<TableRow key={value.id}>
													<TableCell>{globalTranslate(value.typeId)}</TableCell>
													<TableCell>{value.reference}</TableCell>
													<TableCell align="center">{format(parseISO(value.dueDate), "dd/MM/yyyy")}</TableCell>
													<TableCell align="right">{formatScaleValueBR(String(getSinisterValueAmount(value)))}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>

							<Divider />

							<Box>
								<Typography variant="subtitle2" color="primary" fontWeight={700} sx={sectionTitleSx}>
									Parcelas de Pagamento
								</Typography>
								{installmentsCount === 1 ? (
									<Typography variant="body2">
										À Vista — Vencimento em {dueDate ? format(parseISO(dueDate), "dd/MM/yyyy") : "-"} — {formatScaleValueBR(String(installmentValue))}
									</Typography>
								) : (
									<TableContainer>
										<Table size="small">
											<TableHead>
												<TableRow>
													<TableCell>Parcela</TableCell>
													<TableCell align="center">Vencimento</TableCell>
													<TableCell align="right">Valor</TableCell>
												</TableRow>
											</TableHead>
											<TableBody>
												{installmentSchedule.map((installment) => (
													<TableRow key={installment.number}>
														<TableCell>{installment.number}</TableCell>
														<TableCell align="center">{format(installment.dueDate, "dd/MM/yyyy")}</TableCell>
														<TableCell align="right">{formatScaleValueBR(String(installment.value))}</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									</TableContainer>
								)}
							</Box>

							<Divider />

							<Box display="flex" justifyContent="space-between" alignItems="center">
								<Typography variant="subtitle1" fontWeight={700}>
									Valor Total do Acordo
								</Typography>
								<Typography variant="subtitle1" fontWeight={700}>
									{formatScaleValueBR(String(totalValue))}
								</Typography>
							</Box>

							<Box display="flex" justifyContent="flex-end" gap={1.5}>
								<Button variant="outlined" onClick={() => setStep("build")}>
									Voltar
								</Button>
								<Button
									variant="contained"
									disabled={isSubmitting}
									startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
									onClick={handleConfirm}
								>
									Confirmar Acordo
								</Button>
							</Box>
						</Stack>
					)}
				</DialogContent>
			</Dialog>

			<ModalSinisterValues open={openDebtDetails} sinisterId={sinisterId} onClose={() => setOpenDebtDetails(false)} />

			<Dialog open={openInstallmentsPreview} onClose={() => setOpenInstallmentsPreview(false)} maxWidth="xs" fullWidth>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
						Parcelas da Negociação
					</Typography>
					<IconButton onClick={() => setOpenInstallmentsPreview(false)} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>
				<DialogContent dividers>
					<TableContainer>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Parcela</TableCell>
									<TableCell align="center">Vencimento</TableCell>
									<TableCell align="right">Valor</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{installmentSchedule.map((installment) => (
									<TableRow key={installment.number}>
										<TableCell>{installment.number}</TableCell>
										<TableCell align="center">{format(installment.dueDate, "dd/MM/yyyy")}</TableCell>
										<TableCell align="right">{formatScaleValueBR(String(installment.value))}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</DialogContent>
			</Dialog>
		</>
	);
};
